import { ClerkFailed, ClerkProvider } from "@clerk/react";
import { Suspense, useCallback, useEffect, useState } from "react";

/**
 * `ClerkProvider` that reloads clerk-js when the script fails to arrive.
 *
 * `loadClerkJsScript` makes exactly one attempt: on failure it emits status
 * `error` and stops, so `useAuth().isLoaded` never flips and the sign-in CTA
 * stays inert until the visitor thinks to reload the page. That is the
 * user-visible half of TYCHO-19 — a third-party script load that fails for
 * ~1% of homepage visitors (slow mobile networks, blockers, transient CDN
 * trouble), leaving the top of the signup funnel silently broken.
 *
 * Remounting is what makes a retry possible: `ClerkProvider`'s unmount effect
 * calls `IsomorphicClerk.clearInstance()`, so once the cached singleton is gone
 * the fresh mount calls `loadClerkJsScript` again. The loader cooperates — it
 * detects the previously-failed `script[data-clerk-js-script]` via the
 * Performance API and removes it before injecting a new one.
 *
 * A bare `key` bump is not enough. `getOrCreateInstance()` only builds a fresh
 * `IsomorphicClerk` when the publishable key changes or the singleton is null,
 * and `clearInstance()` runs in the outgoing provider's *passive-effect
 * cleanup* — after React has already rendered the replacement and read the
 * still-cached, failed singleton. So the replacement would reuse the dead
 * instance and load nothing. We therefore fully unmount the provider for one
 * commit (rendering no `ClerkProvider` at all) so its cleanup nulls the
 * singleton, then mount a fresh one on the next commit. `clearInstance` is not
 * exported by `@clerk/react`, so this unmount/remount is the only supported way
 * to drop it.
 */

const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1_500;

/**
 * Rendered only inside `<ClerkFailed>`, so mounting IS the failure signal —
 * no polling `isLoaded` against a timeout of our own, which would race the
 * loader's internal 15s one and risk remounting while it was still trying.
 */
function RetryOnFailure({ attempt, onRetry }: { attempt: number; onRetry: () => void }) {
  useEffect(() => {
    if (attempt >= MAX_RETRIES) return;
    const id = window.setTimeout(onRetry, RETRY_BASE_DELAY_MS * 2 ** attempt);
    return () => window.clearTimeout(id);
  }, [attempt, onRetry]);

  return null;
}

export function ClerkProviderWithRetry({
  publishableKey,
  children,
}: {
  publishableKey: string;
  children: React.ReactNode;
}) {
  const [attempt, setAttempt] = useState(0);
  // `remounting` gates the one-commit gap that lets the failed singleton clear.
  // A retry sets it; the effect below advances `attempt` and clears it only
  // after the provider has been unmounted for a commit.
  const [remounting, setRemounting] = useState(false);
  const retry = useCallback(() => setRemounting(true), []);

  useEffect(() => {
    if (!remounting) return;
    // This mount effect runs in the same passive phase as — but after — the
    // just-unmounted provider's cleanup, so `clearInstance()` has already
    // nulled the singleton by the time the next render calls
    // `getOrCreateInstance()`. That guarantees a genuinely fresh load.
    setAttempt((a) => a + 1);
    setRemounting(false);
  }, [remounting]);

  // The empty commit: no `ClerkProvider`, so its unmount effect fires and drops
  // the dead singleton. The children go with it, but only ever while Clerk has
  // already reached status `error` — auth is dead and the page is degraded
  // anyway, the same blast radius the remount itself carries.
  if (remounting) return null;

  return (
    // The `key` remounts everything below, including the SpacetimeDB
    // connection. That blast radius is acceptable *because* of when it fires:
    // only once Clerk has already reached status `error`, i.e. auth is dead
    // and the page is degraded anyway. It is bounded to MAX_RETRIES so a
    // genuinely blocked script (an ad blocker, a bot challenge) settles into
    // the same broken state as today rather than remounting forever.
    <ClerkProvider key={attempt} publishableKey={publishableKey}>
      <ClerkFailed>
        <RetryOnFailure attempt={attempt} onRetry={retry} />
      </ClerkFailed>
      {/*
        `{children}` is `SpacetimeDBProviders`, which calls `useClerkToken()` —
        a Suspense hook that throws while `useAuth().isLoaded` is false. On a
        script failure `isLoaded` never flips, so it suspends forever. Without
        this boundary the suspension propagates past `ClerkProvider` (nothing
        above catches it until the app shell), leaving the whole subtree —
        including `RetryOnFailure` — uncommitted, so its effect never runs and
        no retry ever fires. Isolating the suspending children here lets the
        `<ClerkFailed>` sibling commit and drive the retry. `null` fallback
        matches the blank the outer path showed anyway while auth loads.
      */}
      <Suspense fallback={null}>{children}</Suspense>
    </ClerkProvider>
  );
}
